import React, { useState } from "react";
import Uppy from "@uppy/core";
import Webcam from "@uppy/webcam";
import {
  Dashboard as DashboardReactComponent,
  useUppyEvent,
} from "@uppy/react";
import AwsS3 from "@uppy/aws-s3";
import { useGetS3UploadUrlMutation } from "../hooks";
import { OsUppyFile } from "../utils";
import { v4 } from "uuid";
import { nameToRFC1035 } from "@outschool/environment";
import { captureError } from "@outschool/platform";

import "@uppy/core/dist/style.min.css";
import "@uppy/dashboard/dist/style.min.css";
import "@uppy/webcam/dist/style.min.css";

interface UppyComponentProps {
  maxNumberOfFiles?: number;
  maxFileSize?: number;
  width?: number;
  height?: number;
  allowedFileTypes?: string[];
  onFileAdded?: (file: OsUppyFile) => void;
  onFilesUploaded?: (files: OsUppyFile[]) => void;
  onSuccess?: () => void;
}

export const UppyComponent: React.FC<UppyComponentProps> = ({
  maxNumberOfFiles,
  maxFileSize,
  width = 750,
  height = 550,
  allowedFileTypes,
  onFileAdded,
  onFilesUploaded,
  onSuccess,
}) => {
  const { getS3UploadUrl } = useGetS3UploadUrlMutation();

  // none of the props should ever change, so the props aren't supposed to cause a re-render
  const [uppy] = useState(() => {
    return new Uppy({
      restrictions: {
        allowedFileTypes,
        maxFileSize,
        maxNumberOfFiles,
      },
      id: "uppy-instance",
    })
      .use(Webcam)
      .use(AwsS3, {
        shouldUseMultipart: false,
        async getUploadParameters(file: OsUppyFile) {
          try {
            const url = await getS3UploadUrl(file.name!);

            if (!url) {
              const e = new Error(
                `No pre-signed URL returned for ${file.name}`
              );
              captureError(e, {
                extra: {
                  fileName: file.name,
                },
              });
              uppy.removeFile(file.id);
              throw e;
            }

            return {
              method: "PUT",
              url,
              headers: {
                "Content-Type": file.type,
              },
            };
          } catch (error) {
            console.error(
              `Failed to get pre-signed URL for ${file.name}`,
              error
            );
            captureError(error);
            uppy.removeFile(file.id);
            throw error;
          }
        },
      });
  });

  useUppyEvent(uppy, "complete", async (result: any) => {
    if (result?.successful?.length > 0 && onFilesUploaded) {
      await onFilesUploaded(result.successful);
    }

    if (result?.failed?.length === 0) {
      if (onSuccess) {
        onSuccess();
      }
    }
  });

  useUppyEvent(uppy, "file-added", async (file: OsUppyFile) => {
    try {
      validateFileSize(file);
    } catch (error: any) {
      uppy.info(error.message, "error", 5000); // Show error in Uppy UI for 5 seconds
      uppy.removeFile(file.id); // Remove the invalid file
      return;
    }

    const fileExtFromUppy = file.extension;
    const fileParts = file.name!.split(".");

    // add uuid to the filename to avoid conflicts
    // and always use the extension from uppy
    const newFilename = `${nameToRFC1035(
      fileParts.slice(0, -1).join(".")
    )}_${v4()}.${fileExtFromUppy}`;
    uppy.setFileMeta(file.id, { name: newFilename });
    uppy.setFileState(file.id, { name: newFilename });
    if (file && onFileAdded) {
      await onFileAdded(file);
    }
  });

  return (
    <DashboardReactComponent
      uppy={uppy}
      width={width}
      height={height}
      proudlyDisplayPoweredByUppy={false}
    />
  );
};

function validateFileSize(file: OsUppyFile) {
  if (file.type.startsWith("image/")) {
    if (file.size && file.size > 10 * 1024 * 1024) {
      throw new Error(`File size exceeds the maximum allowed size of 10 MB`);
    }
  } else if (file.type.startsWith("video/")) {
    if (file.size && file.size > 1000 * 1024 * 1024) {
      throw new Error(`File size exceeds the maximum allowed size of 1000 MB`);
    }
  } else if (file.size && file.size > 50 * 1024 * 1024) {
    throw new Error(`File size exceeds the maximum allowed size of 50 MB`);
  }
}
